import { FC, useContext, useEffect, useRef, useState } from 'react';
import { isNotEmptyAny } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import UserInfoContext from '@/layout/context';
import { globalAlias, globalExpansionTaskAlias, globalFranchiseeAlias } from '@/common/utils/ways';
import V2Tabs from '@/common/components/Data/V2Tabs';
import V2Flex from '@/common/components/Feedback/V2Flex';
import TaskDetailApproveInfo from '@/common/components/business/ExpandStore/TaskDetailApproveInfo';
import TaskTerminateInfo from '@/common/components/business/ExpandStore/TaskTerminateInfo';
import PointMatching from '@/common/components/business/ExpandStore/TaskDeatil/components/PointMatching';
import Meet from '@/common/components/business/ExpandStore/TaskDeatil/components/Meet';
import FranchiseeInfo from '@/common/components/business/ExpandStore/TaskDeatil/components/FranchiseeInfo';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import { TaskStatus } from '@/common/components/business/ExpandStore/ts-config';
import TaskInfo from './TaskInfo';
import TaskOverview from './TaskOverview';
import styles from '@/common/components/business/ExpandStore/TaskDeatil/index.module.less';

enum TabKeys {
  /** 机会点 */
  changePoint = 'changePoint',
  /** 沟通记录 */
  communication = 'communication',
  /** 任务信息 */
  taskInfo = 'taskInfo',
  franchisee = 'franchisee',
}

/** 拓店任务：加盟（三津汤包）-详情，不含顶部操作按钮 */
const TaskDetail: FC<{
  /** 拓店任务详情信息 */
  detail: any;
  /** 审批详情 */
  aprDetail?: any;
  /** 主动刷新页面 */
  refresh?: Function;
  /** 是否需要主动隐藏操作按钮 */
  hideOperate?: boolean;
}> = ({
  detail,
  aprDetail = {},
  refresh,
  hideOperate = false,
}) => {
  const [chancePointInfo, setChancePointInfo] = useState<any>(null);
  const [franDetail, setFranDetail] = useState<any>({});
  const [active, setActive] = useState<TabKeys>(TabKeys.changePoint);
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const expansionTaskAlias = globalExpansionTaskAlias(globalAliasItemVOs);
  const pointMatchingRef = useRef<any>(null);
  const meetRef = useRef<any>(null);
  /** 展示外部的新增机会点按钮文案，为空则不展示按钮 */
  const [addPointButtonText, setAddPointButtonText] = useState('');
  /** 展示外部的新增沟通记录按钮文案，为空则不展示按钮 */
  const [addMeetButtonText, setAddMeetButtonText] = useState('');

  // 是否只能查看不能新增数据（拓店任务状态非进行中时）
  const isView = detail.status !== TaskStatus.Processing || hideOperate;

  useEffect(() => {
    const franchiseeId = detail?.franchiseeDetail?.id || detail?.standardFranchiseeId;
    franchiseeId && methods.getFranDetail(franchiseeId);
  }, [detail?.franchiseeDetail?.id, detail?.standardFranchiseeId]);

  useEffect(() => {
    // 待指派时优先展示任务信息，方便操作指派经理
    if (detail?.status === TaskStatus.WaitAssign) {
      setActive(TabKeys.taskInfo);
    }
  }, [detail?.status]);

  const methods = useMethods({
    /** 获取加盟商详情 */
    getFranDetail(id: number) {
      getFranchiseeDetail({ id }).then((res) => {
        res && setFranDetail(res);
      });
    },
    getTabItems() {
      const tabChance = {
        key: TabKeys.changePoint,
        label: `${globalAlias(globalAliasItemVOs)} ${chancePointInfo?.objectList?.length || 0}`,
      };
      const tabCommu = {
        key: TabKeys.communication,
        label: '沟通记录',
      };
      const tabTask = {
        key: TabKeys.taskInfo,
        label: `${expansionTaskAlias || '拓店任务'}信息`,
      };
      const tabFran = {
        key: TabKeys.franchisee,
        label: `${franchiseeAlias}信息`,
      };
      // 待指派时优先展示任务信息，方便操作指派经理
      return detail?.status === TaskStatus.WaitAssign ? [tabTask, tabChance, tabCommu, tabFran] : [tabChance, tabCommu, tabTask, tabFran];
    },
    /** 新增机会点 */
    addChancePoint() {
      pointMatchingRef?.current?.addChancePoint();
    },
    /** 新增沟通记录 */
    addCommunicationRecord() {
      meetRef?.current?.addCommunicationRecord();
    },
    handleTabChange(value: string) {
      setActive(value as TabKeys);
    },
  });

  const tabItems = methods.getTabItems();

  return isNotEmptyAny(detail) ? <div className={styles.taskDetail}>
    <TaskTerminateInfo detail={detail} />

    {/* 异动审批信息 */}
    <TaskDetailApproveInfo
      detail={detail}
      aprDetail={aprDetail}
      isApproval={false}
    />

    <TaskOverview
      detail={detail}
    />

    <V2Tabs
      items={tabItems}
      tabBarExtraContent={{
        right: <V2Flex gap={10}>
          {active === TabKeys.changePoint && !isView && addPointButtonText && <a onClick={methods.addChancePoint}>{addPointButtonText}</a>}
          {active === TabKeys.communication && !isView && addMeetButtonText && <a onClick={methods.addCommunicationRecord}>{addMeetButtonText}</a>}
        </V2Flex>
      }}
      onChange={methods.handleTabChange}
    />

    {/* 任务信息 */}
    {active === TabKeys.taskInfo ? <TaskInfo detail={detail} /> : <></>}

    {/* 匹配点位-机会点 */}
    {active === TabKeys.changePoint ? <PointMatching
      ref={pointMatchingRef}
      id={detail.id}
      taskDetail={detail}
      refresh={refresh}
      chancePointInfo={chancePointInfo}
      setChancePointInfo={setChancePointInfo}
      hideOperate={hideOperate}
      isView={isView}
      showAddBtn={false}
      setAddPointButtonText={setAddPointButtonText}
    /> : <></>}

    {/* 沟通记录 */}
    {active === TabKeys.communication ? <Meet
      ref={meetRef}
      id={detail.id}
      isView={isView}
      showAddBtn={false}
      setAddMeetButtonText={setAddMeetButtonText}
    /> : <></>}

    {active === TabKeys.franchisee ? <FranchiseeInfo franDetail={franDetail} /> : <></>}
  </div> : <></>;
};

export default TaskDetail;
